/**
 * Optimisme lu/non-lu face aux sync en vol.
 *
 * Politique :
 * - Garder le pending jusqu’au TTL (HOLD) — ne pas clear au 1er fetch IMAP « confirmé »
 *   (sinon un batch hors-ordre / sticky expiré peut redescendre seen:false).
 * - Ne jamais clear sur snapshot optimiste / DD02 (`imapConfirmed === false`).
 * - Filet serveur : sticky DD02 après mark-seen/unread (TTL long).
 */

export type PendingSeenEntry = { seen: boolean; at: number };

/** Filet max — aligne le sticky serveur (~10 min) pour les allers-retours de compte. */
export const MAIL_PENDING_SEEN_HOLD_MS = 10 * 60_000;

/**
 * Avant, on clearait dès 2,5–10 s au 1er match IMAP → course hors-ordre.
 * Désormais : pas de clear anticipé (CONFIRM = HOLD).
 */
export const MAIL_PENDING_SEEN_CONFIRM_MIN_MS = MAIL_PENDING_SEEN_HOLD_MS;

export function setPendingSeenEntry(map: Map<number, PendingSeenEntry>, uid: number, seen: boolean): void {
  map.set(uid, { seen, at: Date.now() });
}

export function setPendingSeenEntryKey(map: Map<string, PendingSeenEntry>, key: string, seen: boolean): void {
  map.set(key, { seen, at: Date.now() });
}

export function prunePendingSeenMap(map: Map<number, PendingSeenEntry>, now = Date.now()): void {
  for (const [uid, entry] of [...map.entries()]) {
    if (now - entry.at >= MAIL_PENDING_SEEN_HOLD_MS) {
      map.delete(uid);
    }
  }
}

export function prunePendingSeenMapKeys(map: Map<string, PendingSeenEntry>, now = Date.now()): void {
  for (const [key, entry] of [...map.entries()]) {
    if (now - entry.at >= MAIL_PENDING_SEEN_HOLD_MS) {
      map.delete(key);
    }
  }
}

export function getPendingSeenValue(
  map: Map<number, PendingSeenEntry>,
  uid: number,
  now = Date.now(),
): boolean | undefined {
  const entry = map.get(uid);
  if (!entry) return undefined;
  if (now - entry.at >= MAIL_PENDING_SEEN_HOLD_MS) {
    map.delete(uid);
    return undefined;
  }
  return entry.seen;
}

export function getPendingSeenValueKey(
  map: Map<string, PendingSeenEntry>,
  key: string,
  now = Date.now(),
): boolean | undefined {
  const entry = map.get(key);
  if (!entry) return undefined;
  if (now - entry.at >= MAIL_PENDING_SEEN_HOLD_MS) {
    map.delete(key);
    return undefined;
  }
  return entry.seen;
}

/**
 * Retire le pending quand un fetch IMAP réel confirme le même flag.
 * @param imapConfirmed false pour cache/optimiste — ne pas clear.
 */
export function reconcilePendingSeenEntries(
  map: Map<number, PendingSeenEntry>,
  messages: ReadonlyArray<{ uid: number; seen: boolean }>,
  imapConfirmed: boolean,
  now = Date.now(),
): void {
  if (!imapConfirmed || map.size === 0) return;

  for (const msg of messages) {
    const entry = map.get(msg.uid);
    if (!entry) continue;
    if (now - entry.at < MAIL_PENDING_SEEN_CONFIRM_MIN_MS) continue;
    if (msg.seen === entry.seen) {
      map.delete(msg.uid);
    }
  }
}

/** Variante clés `accountId:uid` (boîte unifiée). */
export function reconcilePendingSeenEntriesKeys(
  map: Map<string, PendingSeenEntry>,
  messages: ReadonlyArray<{ key: string; seen: boolean }>,
  imapConfirmed: boolean,
  now = Date.now(),
): void {
  if (!imapConfirmed || map.size === 0) return;

  for (const msg of messages) {
    const entry = map.get(msg.key);
    if (!entry) continue;
    if (now - entry.at < MAIL_PENDING_SEEN_CONFIRM_MIN_MS) continue;
    if (msg.seen === entry.seen) {
      map.delete(msg.key);
    }
  }
}
